import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FiHeart, FiUsers, FiHome } from 'react-icons/fi';

const Section = styled.section`
  padding: 100px 0;
  background: linear-gradient(135deg, var(--light-gray) 0%, #f8fafc 50%, #e2e8f0 100%);
  &#what-is-burrito-sewa {
    scroll-margin-top: 80px;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const SectionTitle = styled(motion.h2)`
  text-align: center;
  font-size: 3rem;
  color: var(--primary-blue);
  margin-bottom: 24px;

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const SectionSubtitle = styled(motion.p)`
  text-align: center;
  font-size: 1.3rem;
  color: var(--medium-gray);
  max-width: 800px;
  margin: 0 auto 60px;
  line-height: 1.6;
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 80px;
  align-items: center;
  margin-bottom: 80px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 40px;
  }
`;

const TextContent = styled(motion.div)`
  h3 {
    color: var(--primary-blue);
    font-size: 2.2rem;
    margin-bottom: 24px;
  }

  p {
    font-size: 1.1rem;
    line-height: 1.8;
    margin-bottom: 20px;
  }

  ul {
    list-style: none;
    padding: 0;
    
    li {
      display: flex;
      align-items: flex-start;
      gap: 12px;
      margin-bottom: 16px;
      font-size: 1.1rem;
      line-height: 1.6;
      
      &:before {
        content: "✓";
        color: var(--primary-orange);
        font-weight: bold;
        flex-shrink: 0;
      }
    }
  }
`;

const ImagePlaceholder = styled(motion.div)`
  background: linear-gradient(135deg, var(--primary-orange), var(--warm-gold));
  border-radius: 12px;
  height: 300px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 4rem;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
`;

const FeatureCards = styled(motion.div)`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 40px;
  margin-top: 60px;
`;

const FeatureCard = styled(motion.div)`
  background: white;
  padding: 40px 30px;
  border-radius: 12px;
  text-align: center;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.08);
  transition: transform 0.3s ease;

  &:hover {
    transform: translateY(-5px);
  }
`;

const FeatureIcon = styled.div`
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, var(--primary-orange), var(--warm-gold));
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 24px;
  color: white;
  font-size: 2rem;
`;

const FeatureTitle = styled.h4`
  font-size: 1.4rem;
  color: var(--primary-blue);
  margin-bottom: 16px;
`;

const FeatureDescription = styled.p`
  font-size: 1rem;
  line-height: 1.6;
`;

const WhatIsBurritoSewa = () => {
  return (
    <Section id="what-is-burrito-sewa">
      <Container>
        <SectionTitle
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          What is Ahaar Sewa?
        </SectionTitle>
        
        <SectionSubtitle
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          viewport={{ once: true }}
        >
          Ahaar Sewa is our comprehensive food security initiative serving the Bay Area community. 
          Our flagship program, Burrito Sewa, brings together volunteers to prepare 200-400 vegetarian 
          burritos monthly, partnering with local organizations to distribute nutritious meals to those experiencing food insecurity.
        </SectionSubtitle>

        <ContentGrid>
          <TextContent
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h3>Our Mission</h3>
            <p>
              As part of Sewa International's Bay Area chapter, Ahaar Sewa embodies the spirit 
              of selfless service (Sewa) by addressing food insecurity in our community. Our Burrito Sewa 
              program is the first of many food security initiatives we plan to launch. We believe 
              that no one should go hungry, and every person deserves access to nutritious meals.
            </p>
            
            <p>What makes our approach special:</p>
            <ul>
              <li>100% vegetarian burritos made with fresh, quality ingredients</li>
              <li>Collaborative volunteer events that build community bonds</li>
              <li>Partnerships with local shelters and food distribution centers</li>
              <li>Sustainable approach to addressing food insecurity</li>
              <li>Opportunities for families and individuals to give back together</li>
            </ul>
          </TextContent>

          <ImagePlaceholder
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            🍽️👥
          </ImagePlaceholder>
        </ContentGrid>

        <FeatureCards
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <FeatureCard>
            <FeatureIcon>
              <FiHeart />
            </FeatureIcon>
            <FeatureTitle>Made with Love</FeatureTitle>
            <FeatureDescription>
              Every burrito is prepared with care and compassion by our dedicated volunteers, 
              ensuring quality and nutrition for our community members.
            </FeatureDescription>
          </FeatureCard>

          <FeatureCard>
            <FeatureIcon>
              <FiUsers />
            </FeatureIcon>
            <FeatureTitle>Community Driven</FeatureTitle>
            <FeatureDescription>
              Bringing together volunteers from diverse backgrounds to work towards a common goal 
              of serving those in need and building stronger communities.
            </FeatureDescription>
          </FeatureCard>

          <FeatureCard>
            <FeatureIcon>
              <FiHome />
            </FeatureIcon>
            <FeatureTitle>Local Impact</FeatureTitle>
            <FeatureDescription>
              Partnering with local shelters, food banks, and community centers to ensure our 
              burritos reach those who need them most in the Bay Area.
            </FeatureDescription>
          </FeatureCard>
        </FeatureCards>
      </Container>
    </Section>
  );
};

export default WhatIsBurritoSewa; 